import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { LinearProgressBar } from 'components'
import { theme } from 'config'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { DataType, UserType } from 'types'
import { TruncatedText } from './common/truncated-text'
import { Icon } from './icon'


export interface ICardProps {
  type?:            DataType
  title?:           string
  category?:        string
  userNumber?:      number
  finishedUsers?:   number
  description?:     string
  progress?:        number
  managerProgress?: number
  userType?:        UserType
  dueDate?:         string | Date
  onClick?:         () => void
}

export const ContentCard = observer((props: ICardProps) => {
  const {
    title,
    description,
    progress,
    managerProgress,
    finishedUsers,
    onClick,
    dueDate,
    userNumber,
    type,
    userType,
    category,
  } = props;
  const { t } = useTranslation();

  const showProgress =
    progress !== undefined &&
    (userType === UserType.Manager || userType === UserType.Participant);

  const showManagerProgress = managerProgress != undefined;

  const showUserNumber = userNumber != undefined;

  const themeColor =
    type === DataType.Package
      ? theme.palette.purple.main
      : type === DataType.Program
      ? theme.palette.green.main
      : theme.palette.blue.main;

  const header_title = type === DataType.Package 
    ? "Swedsec ÅKU"
    : t(type);

  return (
    <Card onClick={onClick}>
      <CardHeader
        sx={{
          background: themeColor,
        }}
      >
        {header_title}
      </CardHeader>
      <CardContent>
        <Box>
          {category && (
            <Box mb={1} sx={{ marginTop: '-0.5rem' }}>
              <Typography variant='body2' fontWeight={600} color={themeColor}>
                {category}
              </Typography>
            </Box>
          )}
          <TruncatedText 
            textTransform = 'uppercase'
            variant       = 'h5'
            lines         = {2}
          >
            {title}
          </TruncatedText>
          {description && (
            <Box mt={1}>
              <TruncatedText 
                variant  = 'body2'
                lines    = {2}
                fontSize = {16}
              >
                {description}
              </TruncatedText>
            </Box>
          )}
          {dueDate && (
            <Box
              display    = 'flex'
              alignItems = 'center'
              color      = 'text.secondary'
              mt         = {1}
            >
              <Icon iconId='due-date' size='size-24' />
              <Typography variant='body2' color='text.secondary'>
                {typeof dueDate === "string" ? dueDate : dueDate.toISOString()}
              </Typography>
            </Box>
          )}
          {showUserNumber && (
            <Box
              display    = 'flex'
              alignItems = 'center'
              color      = 'text.secondary'
              mt         = {1}
              gap        = {1}
            >
              <Icon iconId='users-circle' size='size-24' />
              <Typography variant='body2' color='text.secondary'>
                {userNumber} {t('participants')}
              </Typography>
            </Box>
          )}
        </Box>
        {showProgress && (
          <Box mt={5}>
            <LinearProgressBar 
              label = {`${Math.round(progress)} %`}
              value = {Math.round(progress)}
              completed = {Math.round(progress) === 100}
            />
          </Box>
        )}
        {showManagerProgress && (
          <Box mt={5}>
            <LinearProgressBar 
              completed = {Math.round(managerProgress) === 100}
              value = {Math.round(managerProgress)}
              label = {`${finishedUsers || 0} / ${userNumber} ${t(
                'finished'
              )}`} 
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
});

const Card = styled.div`
  display:        flex;
  flex-direction: column;
  flex:           1;
  min-height:     22rem;

  ${(p) =>
    p.onClick !== undefined &&
    css`
      cursor: pointer;
      transform: translateY(0);
      transition: transform 0.2s ease;
      &:hover {
        transform: translateY(-3px);
      }
    `}
`;

const CardHeader = styled(Box)`
  min-height:     25px;
  width:          100%;
  border-radius:  6px 6px 0 0;
  padding:        8px 16px;
  color:          #FFF;
  font-size:      14px;
  font-style:     normal;
  font-weight:    600;
  line-height:    14px;
  text-transform: uppercase;
`;

const CardContent = styled.div`
  flex:             1;
  display:          flex;
  flex-direction:   column;
  justify-content:  space-between;
  padding:          1.5rem;
  background-color: white;
  border-radius:    0 0 6px 6px;
`;